import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  CircleAlert,
  LifeBuoy,
  Mail,
  MessageSquareText,
  Send,
  Sparkles,
  TicketCheck,
  UserRound,
  X,
} from 'lucide-react';
import { createGlobalTicket, TicketCategory, TicketPriority } from '../support/supportService';

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

type TicketDraft = {
  subject: string;
  message: string;
  category: TicketCategory;
  priority: TicketPriority;
};

type AgentResponse = {
  answer: string;
  offerTicket: boolean;
  ticketDraft: TicketDraft | null;
};

const emptyTicket = {
  name: '',
  email: '',
  subject: '',
  message: '',
  category: 'technical' as TicketCategory,
  priority: 'medium' as TicketPriority,
};

const ClientHelpDesk: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'ask' | 'ticket'>('ask');
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: 'Hi, I’m the NWS Client Concierge. Ask me about services, managed applications, the arcade, or getting support.',
    },
  ]);
  const [draft, setDraft] = useState<TicketDraft | null>(null);
  const [ticket, setTicket] = useState(emptyTicket);
  const [asking, setAsking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    closeButtonRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [open]);

  const askConcierge = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || asking) return;

    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: trimmedQuestion }];
    setMessages(nextMessages);
    setQuestion('');
    setAsking(true);
    setError('');
    setDraft(null);

    try {
      const result = await fetch('/api/support-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: trimmedQuestion,
          history: messages.slice(-6),
        }),
      });
      const payload = await result.json();

      if (!result.ok) {
        throw new Error(payload?.error || 'The concierge could not respond.');
      }

      const agentResponse = payload as AgentResponse;
      setMessages((current) => [...current, { role: 'assistant', content: agentResponse.answer }]);
      if (agentResponse.offerTicket && agentResponse.ticketDraft) {
        setDraft(agentResponse.ticketDraft);
      }
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'The concierge could not respond.');
    } finally {
      setAsking(false);
    }
  };

  const useTicketDraft = () => {
    if (!draft) return;
    setTicket((current) => ({ ...current, ...draft }));
    setMode('ticket');
    setError('');
  };

  const submitTicket = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await createGlobalTicket(ticket);
      setSubmitted(true);
      setTicket(emptyTicket);
      setDraft(null);
    } catch (caughtError) {
      console.error('Help desk ticket submission failed', caughtError);
      setError('We could not submit the ticket. Please try again or email NoWindowShoppingOnline@gmail.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[1200] inline-flex min-h-14 items-center gap-3 rounded-full bg-amber-300 px-5 py-3 font-extrabold text-slate-950 shadow-2xl transition hover:-translate-y-0.5 hover:bg-amber-200 focus:ring-amber-400 sm:bottom-7 sm:right-7"
        aria-label="Open NWS client help desk"
      >
        <Sparkles className="h-5 w-5" />
        <span className="hidden sm:inline">Ask NWS</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[1300] flex items-end justify-end bg-slate-950/45 p-0 sm:p-5" role="presentation">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="client-help-title"
            className="flex h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl bg-[#f8f5ec] shadow-2xl sm:h-[min(760px,calc(100vh-2.5rem))] sm:rounded-xl"
          >
            <header className="flex items-center justify-between bg-[#071b24] px-5 py-4 text-white">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-300 text-slate-950">
                  <LifeBuoy className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-300">Client help desk</p>
                  <h2 id="client-help-title" className="text-lg font-extrabold">Ask or submit a ticket</h2>
                </div>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white"
                aria-label="Close client help desk"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="grid grid-cols-2 border-b border-slate-200 bg-white p-2">
              <button
                type="button"
                onClick={() => { setMode('ask'); setError(''); }}
                className={`min-h-11 rounded-md text-sm font-extrabold ${mode === 'ask' ? 'bg-teal-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                Ask the concierge
              </button>
              <button
                type="button"
                onClick={() => { setMode('ticket'); setError(''); }}
                className={`min-h-11 rounded-md text-sm font-extrabold ${mode === 'ticket' ? 'bg-teal-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                Submit a ticket
              </button>
            </div>

            {mode === 'ask' ? (
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex-1 space-y-3 overflow-y-auto p-5" aria-live="polite">
                  {messages.map((message, index) => (
                    <div
                      key={`${message.role}-${index}`}
                      className={`max-w-[88%] rounded-lg px-4 py-3 text-sm leading-6 ${
                        message.role === 'assistant'
                          ? 'bg-white text-slate-700 shadow-sm'
                          : 'ml-auto bg-teal-800 text-white'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                  ))}
                  {asking && (
                    <div className="max-w-[88%] rounded-lg bg-white px-4 py-3 text-sm font-semibold text-slate-500 shadow-sm">
                      Working on that…
                    </div>
                  )}
                  {draft && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                      <div className="flex items-start gap-3">
                        <TicketCheck className="mt-0.5 h-5 w-5 flex-none text-amber-800" />
                        <div>
                          <p className="font-extrabold text-slate-950">A support ticket may help.</p>
                          <p className="mt-1 text-sm leading-6 text-slate-600">I drafted one from your message. Review it before submitting.</p>
                          <button
                            type="button"
                            onClick={useTicketDraft}
                            className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-md bg-slate-950 px-4 py-2 text-sm font-extrabold text-white"
                          >
                            Review ticket draft
                            <ArrowRight className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <form onSubmit={askConcierge} className="border-t border-slate-200 bg-white p-4">
                  {error && <ErrorMessage message={error} />}
                  <label htmlFor="concierge-question" className="sr-only">Ask the NWS concierge</label>
                  <div className="flex gap-2">
                    <textarea
                      id="concierge-question"
                      value={question}
                      onChange={(event) => setQuestion(event.target.value)}
                      rows={2}
                      maxLength={1600}
                      placeholder="Ask about a service, application, project, or problem…"
                      className="min-h-14 flex-1 resize-none rounded-md border border-slate-300 px-3 py-3 text-base outline-none focus:border-teal-700 focus:ring-2 focus:ring-teal-700/20"
                    />
                    <button
                      type="submit"
                      disabled={asking || !question.trim()}
                      className="flex w-14 items-center justify-center rounded-md bg-teal-800 text-white disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Send question"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Do not share passwords, payment-card details, or other sensitive information.</p>
                </form>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-5">
                {submitted ? (
                  <div className="flex h-full flex-col items-center justify-center text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-lg bg-teal-800 text-white">
                      <CheckCircle2 className="h-7 w-7" />
                    </span>
                    <h3 className="mt-5 text-2xl font-extrabold">Ticket submitted.</h3>
                    <p className="mt-3 max-w-sm text-sm leading-6 text-slate-600">Your request is now ready for human review. Watch your email for a response.</p>
                    <button
                      type="button"
                      onClick={() => { setSubmitted(false); setMode('ask'); }}
                      className="mt-6 min-h-11 rounded-md bg-slate-950 px-5 py-2 font-extrabold text-white"
                    >
                      Ask another question
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submitTicket} className="space-y-4">
                    <div>
                      <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-teal-800">Human follow-up</p>
                      <h3 className="mt-2 text-2xl font-extrabold">Submit a support ticket</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-600">Review every field before sending. The concierge cannot submit this for you.</p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Full name" icon={UserRound}>
                        <input required minLength={2} maxLength={120} value={ticket.name} onChange={(event) => setTicket({ ...ticket, name: event.target.value })} className="help-input" placeholder="Your name" />
                      </Field>
                      <Field label="Email" icon={Mail}>
                        <input required type="email" maxLength={254} value={ticket.email} onChange={(event) => setTicket({ ...ticket, email: event.target.value })} className="help-input" placeholder="you@example.com" />
                      </Field>
                    </div>
                    <Field label="Subject" icon={MessageSquareText}>
                      <input required minLength={3} maxLength={180} value={ticket.subject} onChange={(event) => setTicket({ ...ticket, subject: event.target.value })} className="help-input" placeholder="What needs attention?" />
                    </Field>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <label className="text-sm font-bold text-slate-700">
                        Category
                        <select value={ticket.category} onChange={(event) => setTicket({ ...ticket, category: event.target.value as TicketCategory })} className="help-input mt-2">
                          <option value="technical">Technical</option>
                          <option value="billing">Billing</option>
                          <option value="coaching">Coaching</option>
                          <option value="webstore">Web store</option>
                          <option value="raffle">Raffle</option>
                          <option value="other">Other</option>
                        </select>
                      </label>
                      <label className="text-sm font-bold text-slate-700">
                        Priority
                        <select value={ticket.priority} onChange={(event) => setTicket({ ...ticket, priority: event.target.value as TicketPriority })} className="help-input mt-2">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </label>
                    </div>
                    <label className="block text-sm font-bold text-slate-700">
                      Details
                      <textarea required minLength={5} maxLength={5000} rows={6} value={ticket.message} onChange={(event) => setTicket({ ...ticket, message: event.target.value })} className="help-input mt-2 resize-none" placeholder="What happened, what did you expect, and what would help?" />
                    </label>
                    {error && <ErrorMessage message={error} />}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-teal-800 px-5 py-3 font-extrabold text-white hover:bg-teal-900 disabled:opacity-60"
                    >
                      <TicketCheck className="h-5 w-5" />
                      {submitting ? 'Submitting…' : 'Submit ticket'}
                    </button>
                  </form>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
};

const Field = ({ label, icon: Icon, children }: { label: string; icon: React.ElementType<{ className?: string }>; children: React.ReactNode }) => (
  <label className="block text-sm font-bold text-slate-700">
    <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-teal-800" />{label}</span>
    <span className="mt-2 block">{children}</span>
  </label>
);

const ErrorMessage = ({ message }: { message: string }) => (
  <div className="mb-3 flex gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm font-semibold leading-5 text-rose-700">
    <CircleAlert className="mt-0.5 h-4 w-4 flex-none" />
    {message}
  </div>
);

export default ClientHelpDesk;
